import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { CircularProgress, Menu, MenuItem, Checkbox } from "@mui/material";
import { Permissions } from "../../../../permissions";
import { useFetchCustomerRequestsQuery } from "../../../../services/baseQuery";
import { useNavigate } from "react-router-dom";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 773,
  bgcolor: "background.paper",
  border: "2px solid #ffffff",
  boxShadow: 24,
  borderRadius: 2,
};

export const defaultColumns = [
  { id: "businessName", label: "Firm Name", minWidth: 140, required: true },
  { id: "createdAt", label: "Request Date", minWidth: 120, required: false },
  {
    id: "registeredPhoneNo",
    label: "Phone Number",
    minWidth: 140,
    required: true,
  },
  { id: "shopAddress", label: "Shop Address", minWidth: 200, required: false },
  { id: "storeType", label: "Store Type", minWidth: 120, required: false },
  { id: "ownerName", label: "Owner Name", minWidth: 140, required: false },
  { id: "GSTIN", label: "GSTIN", minWidth: 160, required: false },
  { id: "status", label: "Status", minWidth: 100, required: true },
  { id: "action", label: "Action", minWidth: 120, required: true },
];

export default function CustomerRequestsTable({
  searchQuery,
  setRefetchFn,
  anchorEl,
  onCloseSettings,
  visibleColumns,
  onColumnToggle,
  filters,
}) {
  const navigate = useNavigate();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // Fetch customer requests
  const {
    data: customerRequestsData,
    isLoading,
    refetch,
  } = useFetchCustomerRequestsQuery({
    page: page + 1, // API expects 1-based pagination
    limit: rowsPerPage,
    search: searchQuery,
    filters,
  });

  // Set refetch function for parent component
  React.useEffect(() => {
    setRefetchFn(() => refetch);
  }, [refetch, setRefetchFn]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  let ViewRequest =
    Permissions.find(permission =>
      permission.includes("show_customerRequests"),
    ) !== undefined;

  // Filter visible columns
  const visibleColumnsList = defaultColumns.filter(
    column => visibleColumns[column.id],
  );

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAddress = shopAddress => {
    if (!shopAddress) return "";
    const { localAddress, locality, city_district, state, pincode } =
      shopAddress;
    return `${localAddress}, ${locality}, ${city_district}, ${state} - ${pincode}`;
  };

  const formatStoreType = type => {
    return type === "RETAILER" ? "Retailer" : "Wholesaler";
  };

  const handleViewRequest = request => {
    if (request.status === "APPROVED") {
      // Redirect to customer details page for approved requests
      navigate(`/customer/view/${request.customerStore._id}`);
    } else {
      // Keep existing behavior for pending/rejected requests
      navigate(`/customer/request/${request._id}`);
    }
  };

  const renderActionButton = request => {
    switch (request.status) {
      case "APPROVED":
        return (
          <button
            onClick={() => handleViewRequest(request)}
            className="text-[#5652B0] hover:bg-[#5652B0]/5 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium">
            View Customer
          </button>
        );
      case "REJECTED":
        return (
          <button
            onClick={() => handleViewRequest(request)}
            className="text-gray-600 hover:bg-gray-50 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium">
            View Request
          </button>
        );
      default:
        return (
          <button
            onClick={() => handleViewRequest(request)}
            className="text-[#5652B0] hover:bg-[#5652B0]/5 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium">
            View Request
          </button>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <CircularProgress />
      </div>
    );
  }

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        boxShadow: "none",
        border: "1px solid #E5E7EB",
        borderRadius: "0.5rem",
      }}>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onCloseSettings}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: 200,
          },
        }}>
        <div className="px-3 py-2 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-600">Show Columns</p>
        </div>
        {defaultColumns.map(column => (
          <MenuItem
            key={column.id}
            onClick={() => onColumnToggle(column.id)}
            disabled={column.required}
            dense>
            <Checkbox
              checked={visibleColumns[column.id]}
              disabled={column.required}
              size="small"
              sx={{
                color: "#5652B0",
                "&.Mui-checked": {
                  color: "#5652B0",
                },
              }}
            />
            <span
              className={`text-sm ${column.required ? "text-gray-400" : ""}`}>
              {column.label}
              {column.required && " *"}
            </span>
          </MenuItem>
        ))}
      </Menu>

      <TableContainer
        sx={{ maxHeight: "calc(100vh - 300px)" }}
        className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50">
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {visibleColumnsList.map(column => (
                <TableCell
                  key={column.id}
                  align="center"
                  sx={{
                    minWidth: column.minWidth,
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                  className="font-outfit">
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(customerRequestsData?.data?.customerRequests || []).map(row => (
              <TableRow hover role="checkbox" tabIndex={-1} key={row._id}>
                {visibleColumnsList.map(column => {
                  let value;

                  // Handle nested data structure
                  switch (column.id) {
                    case "businessName":
                    case "registeredPhoneNo":
                    case "ownerName":
                    case "GSTIN":
                      value = row.customerStore[column.id];
                      break;
                    case "storeType":
                      value = formatStoreType(row.customerStore[column.id]);
                      break;
                    case "shopAddress":
                      value = formatAddress(row.customerStore.shopAddress);
                      break;
                    case "createdAt":
                      value = formatDate(row.createdAt);
                      break;
                    case "status":
                      value = row.status;
                      break;
                    default:
                      value = row[column.id];
                  }

                  return (
                    <TableCell
                      key={column.id}
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#4B5563",
                      }}
                      className="font-outfit">
                      {column.id === "status" ? (
                        <div
                          className={`px-3 py-1 rounded-full text-sm font-medium inline-block
                            ${
                              value === "PENDING"
                                ? "bg-yellow-100 text-yellow-800"
                                : value === "APPROVED"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}>
                          {value}
                        </div>
                      ) : column.id === "action" ? (
                        <div className="flex items-center justify-center">
                          {renderActionButton(row)}
                        </div>
                      ) : (
                        value
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={customerRequestsData?.data?.totalCount || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: "1px solid #E5E7EB",
          ".MuiTablePagination-select": {
            borderRadius: "0.375rem",
          },
        }}
      />
    </Paper>
  );
}
